// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Plus, Search, Filter, Calendar, Activity, BarChart3 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import PipelineColumn from '@/components/sales/PipelineColumn';
import LeadForm from '@/components/sales/LeadForm';
import ActivityForm from '@/components/sales/ActivityForm';
import LeadCard from '@/components/sales/LeadCard';

interface PipelineStage {
  id: string;
  name: string;
  order_index: number;
  color: string;
  is_active: boolean;
}

interface Lead {
  id: string;
  sales_rep_id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  contact_title?: string;
  lead_source: string;
  target_segment: string;
  status: string;
  value?: number;
  estimated_close_date?: string;
  priority_level: string;
  notes?: string;
  last_contact_date?: string;
  next_follow_up?: string;
  tags?: string[];
  metadata?: any;
  created_at: string;
  updated_at: string;
}

interface Activity {
  id: string;
  lead_id: string;
  sales_rep_id: string;
  activity_type: string;
  subject?: string;
  description?: string;
  scheduled_date?: string;
  completed_date?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const SalesPipeline: React.FC = () => {
  const { profile } = useAuth();
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterValue, setFilterValue] = useState('all');
  
  // Modal states
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [activityFormOpen, setActivityFormOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  
  // Drag and drop
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    if (profile?.id) {
      loadPipelineData();
    }
  }, [profile?.id]);

  const loadPipelineData = async () => {
    try {
      setLoading(true);

      // Load pipeline stages
      const { data: stagesData, error: stagesError } = await supabase
        .from('sales_pipeline_stages')
        .select('*')
        .eq('is_active', true)
        .order('order_index');

      if (stagesError) throw stagesError;

      // Load leads
      const { data: leadsData, error: leadsError } = await supabase
        .from('sales_leads')
        .select('*')
        .eq('sales_rep_id', profile?.id)
        .order('created_at', { ascending: false });

      if (leadsError) throw leadsError;

      // Load activities
      const { data: activitiesData, error: activitiesError } = await supabase
        .from('sales_activities')
        .select('*')
        .eq('sales_rep_id', profile?.id)
        .order('created_at', { ascending: false });

      if (activitiesError) throw activitiesError;

      setStages(stagesData || []);
      setLeads(leadsData || []);
      setActivities(activitiesData || []);
    } catch (error) {
      console.error('Error loading pipeline data:', error);
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const lead = leads.find(l => l.id === active.id);
    
    setActiveId(active.id as string);
    setActiveLead(lead || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || !active) return;
    
    const leadId = active.id as string;
    const newStageId = over.id as string;
    const newStage = stages.find(s => s.id === newStageId);
    
    if (!newStage) return;
    
    // Use the same status mapping as getLeadsForStage
    const stageStatusMap: { [key: string]: string } = {
      'Cold Leads': 'cold',
      'Warm Prospects': 'warm',
      'Hot Qualified': 'qualified',
      'Proposal Sent': 'proposal',
      'Negotiation': 'negotiation',
      'Closed Won': 'closed_won',
      'Closed Lost': 'closed_lost'
    };
    
    const newStatus = stageStatusMap[newStage.name] || newStage.name.toLowerCase().replace(/\s+/g, '_');
    
    try {
      const { error } = await supabase
        .from('sales_leads')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(prev => prev.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus, updated_at: new Date().toISOString() } : lead
      ));
      
      toast.success(`Lead moved to ${newStage.name}`);
    } catch (error) {
      console.error('Error updating lead status:', error);
      toast.error('Failed to update lead status');
    }
    
    setActiveId(null);
    setActiveLead(null);
  };

  // Lead and activity handlers
  const handleLeadSave = (savedLead: Lead) => {
    if (editingLead?.id) {
      setLeads(prev => prev.map(lead => lead.id === savedLead.id ? savedLead : lead));
    } else {
      setLeads(prev => [savedLead, ...prev]);
    }
    setLeadFormOpen(false);
    setEditingLead(null);
  };

  const handleLeadEdit = (lead: Lead) => {
    setEditingLead(lead);
    setLeadFormOpen(true);
  };

  const handleAddNote = (leadId: string) => {
    setSelectedLeadId(leadId);
    setActivityFormOpen(true);
  };

  const handleScheduleFollowUp = (leadId: string) => {
    setSelectedLeadId(leadId);
    setActivityFormOpen(true);
  };

  const handleActivitySave = (savedActivity: Activity) => {
    setActivities(prev => [savedActivity, ...prev]);
    setActivityFormOpen(false);
    setSelectedLeadId('');
  };

  const getLeadsForStage = (stageName: string) => {
    // Map stage names to actual status values
    const stageStatusMap: { [key: string]: string } = {
      'Cold Leads': 'cold',
      'Warm Prospects': 'warm',
      'Hot Qualified': 'qualified',
      'Proposal Sent': 'proposal',
      'Negotiation': 'negotiation',
      'Closed Won': 'closed_won',
      'Closed Lost': 'closed_lost'
    };
    
    const stageStatus = stageStatusMap[stageName] || stageName.toLowerCase().replace(/\s+/g, '_');
    return leads.filter(lead => {
      const matchesStatus = lead.status === stageStatus;
      const matchesSearch = !searchTerm || 
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.company && lead.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesSourceFilter = filterSource === 'all' || lead.lead_source === filterSource;
      
      const matchesValueFilter = filterValue === 'all' || 
        (filterValue === 'high' && (lead.value || 0) >= 10000) ||
        (filterValue === 'medium' && (lead.value || 0) >= 5000 && (lead.value || 0) < 10000) ||
        (filterValue === 'low' && (lead.value || 0) < 5000);
      
      return matchesStatus && matchesSearch && matchesSourceFilter && matchesValueFilter;
    });
  };

  const getTotalPipelineValue = () => {
    return leads.reduce((sum, lead) => sum + (lead.value || 0), 0);
  };

  const getConversionStats = () => {
    const totalLeads = leads.length;
    const closedWonLeads = leads.filter(l => l.status === 'closed_won').length;
    const conversionRate = totalLeads > 0 ? (closedWonLeads / totalLeads) * 100 : 0;
    
    return {
      totalLeads,
      closedWonLeads,
      conversionRate: Math.round(conversionRate * 100) / 100
    };
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>;
  }

  const stats = getConversionStats();

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Sales Pipeline
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your leads through the sales process
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => setLeadFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Lead
            </Button>
          </div>
        </div>

        {/* Pipeline Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-card rounded-lg border">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium">Total Leads</span>
            </div>
            <p className="text-2xl font-bold mt-1">{leads.length}</p>
          </div>
          
          <div className="p-4 bg-card rounded-lg border">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium">Pipeline Value</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-green-600">
              ${getTotalPipelineValue().toLocaleString()}
            </p>
          </div>
          
          <div className="p-4 bg-card rounded-lg border">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium">Conversion Rate</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-blue-600">
              {stats.conversionRate}%
            </p>
          </div>
          
          <div className="p-4 bg-card rounded-lg border">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium">Activities</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-purple-600">
              {activities.length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="website">Website</SelectItem>
              <SelectItem value="referral">Referral</SelectItem>
              <SelectItem value="cold_outreach">Cold Outreach</SelectItem>
              <SelectItem value="event">Event</SelectItem>
              <SelectItem value="social_media">Social Media</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filterValue} onValueChange={setFilterValue}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Value" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Values</SelectItem>
              <SelectItem value="high">$10,000+</SelectItem>
              <SelectItem value="medium">$5,000 - $10,000</SelectItem>
              <SelectItem value="low">Under $5,000</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Drag and Drop Pipeline */}
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4 min-h-[600px]">
          {stages.map((stage) => {
            const stageLeads = getLeadsForStage(stage.name);
            
            return (
              <PipelineColumn
                key={stage.id}
                stage={stage}
                leads={stageLeads}
                onLeadEdit={handleLeadEdit}
                onAddNote={handleAddNote}
                onScheduleFollowUp={handleScheduleFollowUp}
                onViewDetails={(lead) => console.log('View details:', lead)}
              />
            );
          })}
        </div>
        
        <DragOverlay>
          {activeLead ? (
            <div className="rotate-2 opacity-80">
              <LeadCard 
                lead={activeLead}
                onEdit={() => {}}
                onAddNote={() => {}}
                onScheduleFollowUp={() => {}}
                onViewDetails={() => {}}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Modals */}
      <LeadForm
        open={leadFormOpen}
        onClose={() => {
          setLeadFormOpen(false);
          setEditingLead(null);
        }}
        lead={editingLead}
        onSave={handleLeadSave}
      />
      
      <ActivityForm
        open={activityFormOpen}
        onClose={() => {
          setActivityFormOpen(false);
          setSelectedLeadId('');
        }}
        leadId={selectedLeadId}
        onSave={handleActivitySave}
      />
    </div>
  );
};

export default SalesPipeline;